import React, { useState, useEffect, useRef } from "react";
import { userService } from "../../../_services";
import { FormDialog } from "../../ui/form-dialog";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../ui/tabs";
import { Alert, AlertDescription } from "../../ui/alert";
import { Upload, CheckCircle, AlertCircle } from "lucide-react";

/**
 * Add/Edit Training Video/File Modal
 * Modernized with shadcn Dialog, Tabs, and Form components
 */
function AddEditTrainingVideo({ hideModal, mode, moduleData, videoData, changeInVideo }) {
    const [userId, setUserId] = useState('');
    const [userName, setUserName] = useState('');
    const [videoTitle, setVideoTitle] = useState('');
    const [fileTitle, setFileTitle] = useState('');
    const [videoLink, setVideoLink] = useState('');
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("video");
    const [selectedFile, setSelectedFile] = useState(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        const [userId, userName] = Usermeta_Data.split(':');
        setUserId(userId);
        setUserName(userName);

        if (videoData) {
            setVideoTitle(videoData.mediaTitle);
            setVideoLink(videoData.mediaLink);
        }
    }, [videoData]);

    const handleSubmit = async () => {
        if (activeTab === "video") {
            await addVideo();
        } else {
            await addDocMediaFile();
        }
    };

    const addVideo = async () => {
        const YTregExp = /^(?:https?:\/\/)?(?:m\.|www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/;

        // Validation
        if (!videoTitle || videoTitle.trim() === '') {
            setFormError('Please add title');
            return;
        }

        if (!videoLink || videoLink.trim() === '') {
            setFormError('Please add link');
            return;
        }

        if (!videoLink.match(YTregExp)) {
            setFormError('Please enter valid YouTube URL');
            return;
        }

        setLoading(true);
        setFormError('');
        setFormSuccess('');

        try {
            let updatedUrl = videoLink;
            if (videoLink.split('&').length > 1) {
                updatedUrl = videoLink.split('?')[0] + '?' + videoLink.split('?')[1].split('&').filter((item) => item.includes('v='))[0];
            }

            let postdata;
            if (mode === 'ADD') {
                postdata = await userService.AddMedia(userId, userName, moduleData.objectUID, videoTitle.trim(), updatedUrl);
            } else {
                postdata = await userService.EditMedia(userId, userName, moduleData.objectUID, videoTitle, updatedUrl, videoData.mediaId);
            }

            if (postdata && postdata.length > 0) {
                if (postdata[0].code === '1') {
                    setFormSuccess(postdata[0].message);
                    changeInVideo();
                    setTimeout(() => {
                        hideModal();
                    }, 2000);
                } else {
                    setFormError(postdata[0].message);
                }
            }
        } catch (error) {
            setFormError('An error occurred. Please try again.');
            console.error('Error adding/editing video:', error);
        } finally {
            setLoading(false);
        }
    };

    const addDocMediaFile = async () => {
        if (!fileTitle || fileTitle.trim() === '') {
            setFormError('Please add title');
            return;
        }

        if (!fileInputRef.current || !fileInputRef.current.files || fileInputRef.current.files.length === 0) {
            setFormError("Please select a valid file.");
            return;
        }

        const file = fileInputRef.current.files[0];
        const fileType = file.type;
        const fileSize = file.size;

        // Validation
        const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
        if (!allowedTypes.includes(fileType)) {
            setFormError("Invalid file type. Allowed types: jpg, jpeg, png, pdf.");
            return;
        }

        const maxSize = 5 * 1024 * 1024; // 5MB
        if (fileSize > maxSize) {
            setFormError("File size exceeds the 5MB limit.");
            return;
        }

        setLoading(true);
        setFormError('');
        setFormSuccess('');

        try {
            const formData = new FormData();
            formData.append("userId", userId);
            formData.append("userName", userName);
            formData.append("moduleid", moduleData.objectUID);
            formData.append("doctitle", fileTitle.trim());
            formData.append("file", file);
            formData.append("filetype", fileType);
            formData.append("mediaid", videoData?.mediaId || '');

            const apiCall = mode === 'ADD' ?
                userService.AddDocFileMedia(formData) :
                userService.EditDocFileMedia(formData);

            const postdata = await apiCall;

            if (postdata && postdata.length > 0) {
                if (postdata[0].code === '1') {
                    setFormSuccess(postdata[0].message);
                    changeInVideo();
                    setTimeout(() => {
                        hideModal();
                    }, 2000);
                } else {
                    setFormError(postdata[0].message);
                }
            }
        } catch (error) {
            setFormError("Error uploading file.");
            console.error('Error uploading file:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setFormError('');
        setFormSuccess('');
        setVideoTitle('');
        setFileTitle('');
        setVideoLink('');
        setSelectedFile(null);
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
            setFormError('');
        }
    };

    const triggerFileInput = () => {
        fileInputRef.current?.click();
    };

    const handleClose = () => {
        hideModal();
    };

    return (
        <FormDialog
            open={true}
            onOpenChange={handleClose}
            title={`${mode === "EDIT" ? "Edit" : "Add"} ${activeTab === "video" ? "Video" : "File"} in ${moduleData.moduleName} Module`}
            onSubmit={handleSubmit}
            onCancel={handleClose}
            submitText={`${mode === "EDIT" ? "Update" : "Add"} ${activeTab === "video" ? "Video" : "File"}`}
            loading={loading}
            maxWidth="lg"
        >
            <div className="space-y-4">
                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
                    <TabsList className="grid w-full grid-cols-2 bg-brand-light-1">
                        <TabsTrigger value="video" className="data-[state=active]:bg-brand-accent data-[state=active]:text-white">
                            Video
                        </TabsTrigger>
                        <TabsTrigger value="file" className="data-[state=active]:bg-brand-accent data-[state=active]:text-white">
                            File
                        </TabsTrigger>
                    </TabsList>

                    {/* Video Tab Content */}
                    <TabsContent value="video" className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="videoTitle">Video Title</Label>
                            <Input
                                id="videoTitle"
                                type="text"
                                value={videoTitle}
                                onChange={(e) => {
                                    setVideoTitle(e.target.value);
                                    setFormError('');
                                }}
                                placeholder="Enter video title"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="videoLink">YouTube URL</Label>
                            <Input
                                id="videoLink"
                                type="text"
                                value={videoLink}
                                onChange={(e) => {
                                    setVideoLink(e.target.value);
                                    setFormError('');
                                }}
                                placeholder="https://www.youtube.com/watch?v=..."
                            />
                        </div>
                    </TabsContent>

                    {/* File Tab Content */}
                    <TabsContent value="file" className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="fileTitle">Document Title</Label>
                            <Input
                                id="fileTitle"
                                type="text"
                                value={fileTitle}
                                onChange={(e) => {
                                    setFileTitle(e.target.value);
                                    setFormError('');
                                }}
                                placeholder="Enter document title"
                            />
                        </div>

                        {/* File Upload Area */}
                        <div
                            onClick={triggerFileInput}
                            className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-brand-accent transition-colors"
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                id="file_upload"
                                name="file_upload"
                                className="hidden"
                                accept="image/jpeg, image/png, application/pdf"
                                onChange={handleFileChange}
                            />
                            <div className="flex flex-col items-center gap-2">
                                {selectedFile ? (
                                    <>
                                        <CheckCircle className="h-12 w-12 text-green-500" />
                                        <p className="text-brand-dark font-medium">File Selected</p>
                                        <p className="text-sm text-brand-dark/70">{selectedFile.name}</p>
                                        <p className="text-xs text-brand-dark/50">
                                            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <Upload className="h-12 w-12 text-brand-dark/40" />
                                        <p className="text-brand-dark font-medium">Click to upload file</p>
                                        <p className="text-sm text-brand-dark/70">JPG, PNG, or PDF (Max 5MB)</p>
                                    </>
                                )}
                            </div>
                        </div>
                    </TabsContent>
                </Tabs>

                {/* Error Message */}
                {formError && (
                    <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{formError}</AlertDescription>
                    </Alert>
                )}

                {/* Success Message */}
                {formSuccess && (
                    <Alert variant="default" className="bg-green-50 border-green-200">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <AlertDescription className="text-green-800">
                            {formSuccess}
                        </AlertDescription>
                    </Alert>
                )}
            </div>
        </FormDialog>
    );
}

export default AddEditTrainingVideo;
